/**
 * Service layer — Selections Package documents (docs/SELECTIONS_PACKAGE_DOCUMENTS.md).
 *
 * The Outline Specifications + Schedules rows under a selections milestone's Documents branch.
 * All calls route through /api/selections-documents. kind: 'outline-spec' | 'schedules'.
 */

async function call(action, params) {
  const headers = { 'Content-Type': 'application/json' }
  try { const t = await window.__clerkGetToken?.(); if (t) headers.Authorization = 'Bearer ' + t } catch (_) { /* server rejects */ }
  const res = await fetch('/api/selections-documents', { method: 'POST', headers, body: JSON.stringify({ action, ...params }) })
  const json = await res.json().catch(() => ({ error: 'Bad response from the documents service.' }))
  if (!res.ok || json.error) {
    const err = new Error(json.error || ('Request failed (' + res.status + ')'))
    if (json.code) err.code = json.code   // 'empty' = nothing sent to this milestone in AlderSPEC
    throw err
  }
  return json
}

/** Live review copy (light-red watermark) at a stable URL — Preview, and Markup before completion. → url */
export async function draftSelectionsDoc({ milestoneId, projectId, projectName, kind }) {
  const r = await call('draft', { milestoneId, projectId, projectName, kind })
  return r.url
}

/** "Mark complete": save the review copy as the milestone's document for this row, locked. → document row */
export async function completeSelectionsDoc({ milestoneId, projectId, projectName, kind, actorName }) {
  const r = await call('complete', { milestoneId, projectId, projectName, kind, actorName })
  return r.document
}

/** Posting: re-print every kind from what was issued (overrides the lock). → { ok, results } */
export function issueSelectionsDocs({ milestoneId, projectId, projectName, kinds, issueDate, actorName }) {
  return call('issue', { milestoneId, projectId, projectName, kinds, issueDate, actorName })
}

/**
 * Which kinds are NOT yet marked complete — the posting gate. Reads the milestone's documents
 * through the documents service (one row per kind, source = kind). → string[] of kinds missing.
 */
export async function incompleteSelectionsDocs(milestoneId, kinds) {
  if (!kinds || !kinds.length) return []
  const { getMilestoneDocuments } = await import('./documents.js')
  const res = await getMilestoneDocuments(milestoneId)
  const docs = (res && res.documents) || []
  return kinds.filter(k => !docs.some(d => d.source === k && d.locked))
}

/** Opens the live review copy in a new tab (tab opened synchronously so it isn't popup-blocked). */
export async function previewSelectionsDoc(params) {
  const tab = window.open('', '_blank')
  if (tab) {
    try {
      tab.document.write('<body style="margin:0;font-family:\'DM Sans\',sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;background:#f4f7f8;color:#07272D;font-size:13px;">Preparing review copy…</body>')
      tab.document.close()
    } catch (_) { /* best-effort */ }
  }
  try {
    const url = await draftSelectionsDoc(params)
    if (tab) tab.location = url; else window.open(url, '_blank')
    return { ok: true }
  } catch (e) {
    if (tab) tab.close()
    return { error: e.message || 'Could not prepare the review copy.' }
  }
}
